import type { ApiClient } from "@planner/core";
import { useCallback, useEffect, useState } from "react";

/**
 * **عدّادُ «اللي عليك» — رقمٌ واحد، ودالّةٌ واحدة.**
 *
 * **والرقمُ هو عددُ بنود القائمة نفسِها** (`GET /actions` · `items`) — ما يُعرَض
 * في شاشة «اللي عليك» بالضبط، **لا عدّادٌ ثانٍ يُحسَب في مكانٍ آخر**. فرقمان لنفس
 * السؤال يفترقان: الشريطُ يقول ٣ والشاشةُ تفتح على اثنين.
 *
 * **والمؤجَّلُ ليس منه** — فهو ما أخفاه صاحبُه بيده إلى موعده.
 */
export function pendingActionCount(view: { readonly items: readonly unknown[] }): number {
  return view.items.length;
}

/**
 * **ويُقرأ كلّما تحرّكت القوقعة** — فلا رقمٌ قديمٌ يبقى على الشريط.
 *
 * فمفتاحُ التحديث هو الشاشةُ المفتوحة: **كلُّ انتقالٍ يُعيد سؤالَ السيرفر**، ومن
 * بتّ في قرارٍ ثم رجع يرى الرقمَ الجديد. **وشاشةُ «اللي عليك» تُبلِّغه بما قرأته**
 * (`report`) — فمن أجّل بنداً وهو فيها يرى الشريطَ يتغيّر بلا انتقال.
 *
 * **وفشلُ القراءة لا يرسم صفراً**: يبقى الرقمُ السابق — فصفرٌ يقول «مفيش حاجة
 * عليك» وهو لا يعرف، وهو كذبٌ أسوأ من الصمت. والشاشةُ نفسُها تقول خطأها بكوده.
 */
export function useActionCount(
  client: ApiClient,
  refreshKey: string,
  enabled: boolean,
): { readonly count: number; readonly report: (count: number) => void } {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void (async () => {
      const result = await client.request<{ readonly items: readonly unknown[] }>("/actions", {
        signal: controller.signal,
      });
      if (controller.signal.aborted || !result.ok) return;
      setCount(pendingActionCount(result.data));
    })();
    return () => {
      controller.abort();
    };
  }, [client, refreshKey, enabled]);

  const report = useCallback((next: number) => {
    setCount(next);
  }, []);

  return { count, report };
}
